import { type CharacterClass } from '@d2/core';
import { type RunVisibility } from '@d2/schema';
import type Phaser from 'phaser';
import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';

import { GameOverlay } from '../ui/game-overlay';
import { describeFloorMismatch } from './cave';
import { createGame } from './create-game';
import { publishGameHandle, publishRoomHandle } from './dev-handle';
import { type JoinRefusal, describeJoinFailure, refusalOf } from './join-refusal';
import { gameEndpoint, parseLaunch } from './launch';
import { type RoomLink, joinRoom } from './room-link';
import { NOTHING_LOST, type RunToll, endedRun, runEndedText } from './run-end';
import { type StartedWorld, type WorldStart, openLinked, openSolo } from './world';

const WIDTH = 960;
const HEIGHT = 540;

/**
 * The positioned box the whole game lives in, canvas and panels alike.
 *
 * Both panels are `position: absolute; inset: 0`, which resolves against the
 * nearest **positioned** ancestor — and until this existed there was none: the
 * canvas sat in `App.tsx`'s page-level flex column with padding, and a HUD
 * dropped in as-is anchored to the page and drew itself low and wide of the
 * game. `ARCHITECTURE.md` named this exact cost on the day the HUD moved out of
 * the canvas: *"a DOM overlay needs a positioned container the size of the
 * canvas."*
 *
 * The size is `WIDTH`/`HEIGHT` rather than a pair of numbers in `index.css`,
 * because those are the same two numbers Phaser is built with three lines below.
 * One source of truth: a stylesheet that said 960 and a game that said something
 * else would put the orbs somewhere the cave is not, and nothing would say so.
 *
 * `max-width` is honest rather than complete. Below 960 the canvas shrinks with
 * `Scale.FIT` while this box does not letterbox with it, which is the remaining
 * half of the cost `ARCHITECTURE.md` records — desktop first
 * (`docs/GAME-DESIGN.md`), and stated in the handover rather than papered over.
 */
const STAGE: CSSProperties = { position: 'relative', width: WIDTH, height: HEIGHT, maxWidth: '100%' };

/**
 * Owns the Phaser game's lifetime, the room's, and nothing else.
 *
 * React renders one empty div; Phaser owns everything inside it. The cleanup
 * both destroys the game and empties the container, because React 19's
 * StrictMode mounts effects twice in development and a half-destroyed Phaser
 * game leaves its canvas behind — which would show up as two canvases. The
 * connection is torn down on the same path and for the same reason: a room that
 * is not left holds a seat, and a party of four is four seats.
 *
 * The whole start-up is asynchronous now, because the level is the *server's*:
 * a client cannot draw a floor it has not been told about, and it must not draw
 * one it cannot verify. So nothing is created until a room has answered — which
 * is also what makes the mismatch case simple, since there is no game to tear
 * down when the check fails.
 *
 * ## Which run this is, and who may find it
 *
 * Two props decide that, and they are the two halves of the same question. A
 * `roomId` means *join that one* — it comes from the lobby, which resolved a
 * code into an address a moment ago (`ui/lobby-board.tsx`) — and its absence
 * means *create one*, which is when `visibility` is worth anything at all.
 *
 * Both are effect dependencies, because a different room or a different kind of
 * run is a different connection and not a redraw.
 *
 * The `?room=` URL still works and is still what the browser suite uses, but it
 * is now the **fallback**: a caller that says which room to join has resolved a
 * code and is holding a fresher answer than a query string a player pasted. The
 * two are read in that order and nowhere else.
 *
 * ## Where the account boundary is
 *
 * This component is handed a **token and a character id** and never asks how
 * they were obtained. React owns the account and character screens, Phaser owns
 * the world, and this is the seam (`ARCHITECTURE.md`): everything above it is
 * DOM, everything below it is canvas, and the only things that cross are two
 * strings going down and one callback coming back up.
 *
 * That callback — `onSessionEnded` — is the whole of what this knows about
 * authentication. The room refuses a seat with one code and one word for every
 * way a session can be wrong (`apps/game/src/auth/seat.ts`), so the decision
 * made here is `./join-refusal`'s and nothing more. A refused seat is handed
 * back up so the shell can drop the token and show the sign-in screen. Without
 * that hand-back, a returning player whose token expired overnight sits in
 * front of a dead canvas forever, which is the one failure that has no way out
 * of itself.
 *
 * ## Every other refusal goes up as well, when there is somewhere for it to go
 *
 * `onJoinRefused` is the second half of the same argument, and it exists
 * because of the second half of `TODO.md` #443: the room's **409
 * `character-already-on-this-floor`** is a refusal a player can actually answer
 * — wait a moment, press the button again — and it arrives at a component that
 * has no button. What this can draw is `game-fatal`: a sentence over an empty
 * box with nothing to press, which is the right answer only for a caller with
 * no screen to go back to (the `?solo` sandbox, and anything else that mounts
 * this on its own).
 *
 * So a caller that *has* a way back says so by passing `onJoinRefused`, and
 * gets the refusal as a value rather than a rendered dead end. The fatal panel
 * stays as the fallback for everyone else — nothing became less visible, it
 * just stopped being the only option. The same reasoning as the paragraph
 * above, one refusal along: *"a message about signing in beside a screen with
 * no way to sign in is worse than no message at all"*, and a message about
 * trying again belongs beside the screen that can.
 *
 * ## What a floor mismatch does, and why this is the loud version
 *
 * A regenerated cave that hashes differently from the server's means this build
 * and the room disagree about where the rock is, and every position, route and
 * click after it is a lie (`TODO.md` #170). Three answers were possible and two
 * of them are wrong. *Render anyway* is the one that is definitely wrong: the
 * player would walk confidently through walls the server has, and the first
 * they would know of it is a body that will not go where they click. *Stay
 * connected and draw nothing* is worse than it sounds — the room keeps
 * simulating a body the player can neither see nor control, and in a co-op run
 * that body is a seat somebody else could have used.
 *
 * So the client **leaves the room and says so, in words, where the canvas would
 * have been**: an alert panel naming the seed and what the two sides disagreed
 * about, and a matching `console.error` for whoever is holding the logs.
 * Nothing is drawn at any point, because nothing was ever built. Retrying
 * somewhere else is what an operator would want and needs a second server this
 * deployment does not have — recorded rather than invented.
 *
 * **A disagreement about the stairs is answered the same way, and it can arrive
 * late.** The exit is a function of how wide a body is, which no checksum can
 * see (`./cave`), so two builds using different radii can agree about floor
 * one's exit and disagree about floor two's — the descent is exactly where it
 * surfaces. `StartedWorld.onFloorRefused` is that refusal arriving after the
 * canvas already exists, and it takes the same three steps in the same order:
 * log it, leave the room, and replace the canvas with the sentence. The game is
 * destroyed here rather than left running, because a Phaser scene going on
 * drawing a floor the client has disowned is the *"stay connected and draw
 * nothing"* answer wearing a canvas.
 *
 * ## And the third thing that ends a run, which is not a failure at all
 *
 * The player dies. The design's death penalty ends the run rather than standing
 * the corpse back up, and the seat leaves with close code **4200**
 * (`./run-end`). It arrives on a channel neither of the two above uses — the
 * socket closing, not a rejected join and not a floor check — and the only thing
 * this component decides about it is *which* close it was: `endedRun` answers
 * that, and `onRunEnded` hands it to a caller with a town to go back to. The
 * fallback is the fatal panel again, for the same caller the other two have.
 *
 * **What it cost is latched here, and this is the only place it could be.** The
 * bill is two numbers and a close code is one, so it travels as its own
 * addressed message — sent on the still-open socket immediately *before* the
 * close, in that order, guaranteed by a test in the room rather than by a race
 * (`descent.room.ts`'s `endRun`, `@d2/schema`'s `RunTollPayload`). Two events,
 * one announcement: the message says how much and the close says the run is
 * over, and the player must be told once, when both are true. So the toll is
 * held from the moment it lands until the close reads it.
 *
 * It is held *here* rather than in `./room-link` because pairing the two is a
 * decision, and that module is deliberately the one that makes none — it is
 * also the one this package's unit suite cannot reach. This component is
 * unit-tested on purpose, and the latch is the interesting half of the feature.
 *
 * A 4200 with nothing latched is a contract the room broke: `endRun` only ever
 * ends a run it holds a charge for. It is reported to the console and the run
 * still ends, because going back to town is right whatever was or was not said
 * about the bill — and a toll of nothing is the one thing that can be said with
 * no message, which is why it must be **logged** rather than quietly shown.
 *
 * **Whatever the code, a socket that has closed means the link is given up
 * here.** That is not tidiness. `RoomLink.leave` settles on the SDK's own
 * `onLeave`, which has already fired by the time this runs, so a leave sent
 * afterwards never resolves — and the promise the *next* mount waits on
 * (`released`, below) would never settle with it. The visible bug is the one
 * that matters most: pressing Descend after dying would open nothing, forever.
 */
export interface GameCanvasProps {
  /**
   * The player's access token, or `null` for the **renderer sandbox**
   * (`?solo`), which connects to nothing and so has nobody to be.
   *
   * Passed straight to the room and never read, logged or rendered here. It is
   * a prop rather than something this component fetches for itself precisely so
   * that there is one owner of it (`../account/account-state.ts`) and one place
   * it is dropped.
   *
   * Two strings rather than one object on purpose: they are the effect's
   * dependencies, and a fresh `{token, characterId}` on every render of the
   * parent would tear the room down and rejoin it on every render of the
   * parent.
   */
  readonly token: string | null;
  readonly characterId: string | null;
  /**
   * The class whose skill trees the overlay draws, or `null` when nothing knows
   * one yet — the renderer sandbox has no character at all, and a page restored
   * from a stored session mounts this while the roster request that names the
   * class is still in flight.
   *
   * It comes down from the account screens rather than off the wire because
   * nothing on the wire carries it: `PlayerState` has no class field, and
   * `SERVER_MESSAGE.progression` deliberately carries the *ledger* and not the
   * catalogue. The roster already answered this question at the moment the
   * character was chosen, so the answer is passed down beside the id it belongs
   * to — a third string going down the same seam as the other two.
   *
   * It is not one of the effect's dependencies: arriving late must redraw the
   * skill tree, never tear down a room and rejoin it.
   */
  readonly characterClass: CharacterClass | null;
  /**
   * The room to join, as the directory just answered it — or `null` to create
   * one of this client's own.
   *
   * It **wins over the `?room=` in the URL**, because a caller that has one
   * resolved a code moments ago against the party as it stands now, and a query
   * string is whatever was pasted into the address bar. Absent, the URL still
   * decides, which is what keeps the browser suite's `?room=` co-op path
   * working (`./launch`).
   *
   * An effect dependency: a different room is a different connection.
   */
  readonly roomId?: string | null;
  /**
   * Who may find the run this client is about to **create**, or `null` to ask
   * for nothing — which the server reads as private (`./room-options`).
   *
   * Meaningless alongside a `roomId`, exactly as the seed is: that room decided
   * this when it was created. An effect dependency for the reason above.
   */
  readonly visibility?: RunVisibility | null;
  /** The room refused the seat. The shell drops the token and shows the sign-in screen. */
  readonly onSessionEnded: () => void;
  /**
   * The join failed for a reason that is **not** about who this player is, and
   * the caller would rather show it on a screen of its own than have a panel
   * drawn here (`already-seated`, `unreachable`).
   *
   * Absent, and this draws the message itself — which is what the renderer
   * sandbox wants, having nowhere to go back to. Present, and nothing is drawn
   * here at all: the caller decides what a player is told and where.
   *
   * **Must be stable across renders**, exactly as `onSessionEnded` must: it is
   * one of the effect's dependencies, and a fresh function on every render of
   * the parent would tear the room down and rejoin it on every render of the
   * parent. `useCallback` on the caller's side, or a value that does not change.
   */
  readonly onJoinRefused?: (refusal: JoinRefusal) => void;
  /**
   * **The run is over.** The player died, the penalty was taken and the room
   * closed the seat (`./run-end`).
   *
   * It carries **what the death cost** — the toll as the room charged it, off
   * the message that landed just before the close (`TODO.md` #486). That is the
   * one thing the caller cannot find out for itself: the forfeit is a proportion
   * of the purse as it stood at the moment of death, and the caller's last
   * roster read predates the descent.
   *
   * What it does **not** carry is the purse that survived, and a caller must not
   * work one out from these two: the row it holds is pre-run, the run had been
   * adding to the purse ever since, and the API is the only thing that knows
   * what is left. Re-reading the roster is still the caller's job, exactly as
   * before — the toll says what was taken, the roster says what remains.
   *
   * Absent, and this draws the sentence itself, exactly as it does for a
   * refusal a caller did not want. **Must be stable across renders**, for the
   * reason above it.
   */
  readonly onRunEnded?: (toll: RunToll) => void;
}

/**
 * The tab's one room connection, as a promise that settles when the last mount
 * has given it back.
 *
 * **A remount must not open a socket while the previous one still holds a
 * seat.** The room refuses a second connection playing the same character —
 * `CHARACTER_SEATED`, 409 — because two seats holding one character are two live
 * copies of one set of containers and whichever saved last would decide what the
 * other found (`descent.room.ts`). That is right, and it makes this client's
 * lifetime its own problem to get right.
 *
 * React's StrictMode mounts every effect twice in development, so this effect
 * really does run, tear down and run again. Creating a room hid it — two mounts
 * made two *different* rooms, and the abandoned one disposed itself — but a
 * `?room=` join hit the same floor twice with the same character and the live
 * one was the one refused, which is why `e2e/co-op.spec.ts` is where it surfaced
 * and why nothing else in the suite saw it.
 *
 * So the mounts queue. The first one to be torn down before it ever connected
 * simply never opens a socket at all, and one that did connect is waited for
 * until its socket has actually closed — which is when the room runs `onLeave`
 * and frees the seat.
 *
 * Module scope on purpose: what is being serialised is a *tab's* connection to a
 * room, and two `GameCanvas` instances racing each other would be the same bug
 * wearing a different hat.
 */
let released: Promise<void> = Promise.resolve();

export const GameCanvas = ({
  token,
  characterId,
  characterClass,
  roomId = null,
  visibility = null,
  onSessionEnded,
  onJoinRefused,
  onRunEnded,
}: GameCanvasProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * The code this run is invited by, once the room has said it.
   *
   * State rather than a ref, because it is drawn: it arrives once per seat, at
   * the join, and the overlay puts it over the world from then on. `null` until
   * it lands, and for the whole of the renderer sandbox, which has no room to be
   * invited to.
   */
  const [invite, setInvite] = useState<string | null>(null);
  /**
   * The world this mount started, once it has one.
   *
   * React state rather than a ref because the panels are rendered from it: the
   * feed they subscribe to and the intents they send do not exist until a room
   * has answered, so there is nothing to draw until this is set — which is the
   * same asynchronous start-up the canvas itself waits for.
   */
  const [world, setWorld] = useState<StartedWorld | null>(null);

  useEffect(() => {
    const parent = containerRef.current;
    if (parent === null) return undefined;

    let game: Phaser.Game | null = null;
    let link: RoomLink | null = null;
    /** The effect has been cleaned up: whatever this run holds is not wanted. */
    let abandoned = false;
    /** The start-up has finished, one way or the other, so `link` is final. */
    let settled = false;
    let handedBack = false;
    /**
     * What the room said this death cost, from the moment it says it until the
     * close that ends the run reads it. `null` until then — and, on this
     * server, never `null` once 4200 has arrived. See the header.
     */
    let toll: RunToll | null = null;

    const previous = released;
    let letGo!: () => void;
    released = new Promise<void>((resolve) => {
      letGo = resolve;
    });

    /**
     * Whether this run has been torn down.
     *
     * Read through a call rather than compared in place: the cleanup sets it
     * while `start` is suspended at an `await`, which TypeScript's control flow
     * cannot see — so a second `if (abandoned)` would be narrowed to the value
     * the first one proved and quietly stop being a check at all.
     */
    const givenUp = (): boolean => abandoned;

    /**
     * Gives this run's connection back, once there is nothing more it can be
     * waiting for — and only then lets the next mount start.
     *
     * Both halves have to have happened. A cleanup that ran while the join was
     * still in flight has no room to leave yet, and a join that finished into an
     * abandoned effect has nobody left to want it.
     */
    const handBack = (): void => {
      if (handedBack || !abandoned || !settled) return;
      handedBack = true;

      void Promise.resolve(link?.leave()).finally(letGo);
    };

    /**
     * Takes the canvas down: the game, the handle, the element and the panels.
     *
     * Shared by the two things that can end a live run — a floor this build
     * cannot vouch for, and the run itself ending — because leaving a Phaser
     * scene drawing a floor nothing is connected to is the same wrong answer in
     * both cases.
     */
    const takeDown = (): void => {
      game?.destroy(true);
      game = null;
      publishGameHandle(null);
      parent.replaceChildren();
      setWorld(null);
      // The code belongs to a run that is over. Left standing it would invite
      // people into a room this client has just disowned.
      setInvite(null);
    };

    /**
     * The socket closed. Which close it was decides what a player is told, and
     * `./run-end` is where that decision lives.
     *
     * The link is given up **whatever the code**, before anything else: see the
     * header. A leave sent to a socket that has already closed never settles,
     * and the next mount is waiting on it.
     */
    const onClosed = (code: number): void => {
      link = null;

      if (givenUp() || !endedRun(code)) return;

      // The bill, as the room sent it a moment ago. Nothing was said about it
      // only if the room ended a run it held no charge for, which `endRun`
      // cannot do — so it is logged rather than papered over, and the run still
      // ends, because a player whose seat has closed belongs in town either way.
      if (toll === null) {
        console.error('The run ended without the room saying what it cost.');
      }
      const charged = toll ?? NOTHING_LOST;

      if (onRunEnded !== undefined) {
        onRunEnded(charged);
        return;
      }

      takeDown();
      setFailure(runEndedText(charged));
    };

    const open = async (): Promise<WorldStart> => {
      const launch = parseLaunch(window.location.search);
      if (launch.solo || token === null || characterId === null) return openSolo(launch.seed);

      const joined = await joinRoom({
        endpoint: gameEndpoint(import.meta.env.VITE_GAME_URL),
        // The caller's answer first: it resolved a code against the party as it
        // stands now, where the URL is whatever was pasted. See the header.
        roomId: roomId ?? launch.roomId,
        token,
        characterId,
        // What the URL asked a *created* room for. Requests, all of them: the
        // room grants the seed and refuses the population and the floor size to
        // anybody who cannot present the key its own process was started with
        // (`./room-options`).
        seed: launch.seed,
        population: launch.population,
        size: launch.size,
        fixtureKey: launch.fixtureKey,
        // Not the URL's, and deliberately: who may find a run is a decision a
        // player makes on a screen with the consequence written next to it
        // (`ui/town-screen.tsx`), not something a query string does to them.
        visibility,
      });

      link = joined;
      // Before `onClosed`, and the order is the ordering the room promises:
      // the toll is sent on the open socket and the close follows it. Nothing
      // is announced here — a message is not the end of a run, the close code
      // is, and a player told they had died the instant the bill arrived would
      // be standing in town while the room still held their seat.
      joined.onRunToll((charged: RunToll): void => {
        toll = charged;
      });
      // The code, straight onto the screen. Unlike the toll there is nothing to
      // pair it with and nothing to wait for: it is true from the moment it
      // arrives until the seat is given up, which is the whole time the player
      // might want to read it out.
      joined.onInvite((code: string): void => {
        if (givenUp()) return;

        setInvite(code);
      });
      joined.onClosed(onClosed);
      publishRoomHandle({ roomId: joined.roomId, localId: joined.localId });

      return openLinked(joined);
    };

    const start = async (): Promise<void> => {
      // The previous mount's seat is gone before this one asks for one. See the
      // note above `released`: asking sooner is a refused join, not a race that
      // resolves itself.
      await previous;

      // Torn down before it ever connected — StrictMode's first mount, or a
      // navigation. Nothing was opened, so there is nothing to give back.
      if (givenUp()) return;

      const started = await open();

      // ...and torn down while the socket was being opened. Whatever came back
      // is not wanted; `handBack` is what lets go of it.
      if (givenUp()) return;

      if (!started.ok) {
        const message = describeFloorMismatch(started.mismatch);

        console.error(message);
        void link?.leave();
        // Given up here rather than held for the teardown: this connection is
        // over, and a link that is left behind is a seat nobody is using.
        link = null;
        setFailure(message);
        return;
      }

      // A floor the room descended to that this build cannot vouch for. The
      // same refusal as the one above, arriving after a canvas exists — so it
      // takes the canvas down as well. See the header.
      started.onFloorRefused((mismatch): void => {
        const message = describeFloorMismatch(mismatch);

        console.error(message);
        void link?.leave();
        link = null;
        takeDown();
        setFailure(message);
      });

      game = createGame({ parent, width: WIDTH, height: HEIGHT, view: started.view });
      publishGameHandle(game);
      // After the game, so the panels never appear over an empty box — and only
      // here, so a failed start-up has nothing to draw them from.
      setWorld({
        view: started.view,
        feed: started.feed,
        intents: started.intents,
        onFloorRefused: started.onFloorRefused,
      });
    };

    /**
     * Closing the tab is a decision to leave, not a dropped socket.
     *
     * The room holds a seat for thirty seconds when a connection disappears,
     * because a wifi blip is far more often the cause than a decision
     * (`descent.room.ts`, `onDrop`) — and a party of four is four seats. A
     * consented leave on the way out is the difference between the two, and
     * `pagehide` is the one signal a browser reliably gives for it. Best
     * effort: the reconnection window is still the fallback when it does not
     * fire.
     */
    const onPageHide = (): void => {
      void link?.leave();
    };

    window.addEventListener('pagehide', onPageHide);

    void start()
      .catch((error: unknown) => {
        const refusal = refusalOf(error);
        const message = describeJoinFailure(refusal);

        // The refusal, never the error. A thrown value from a socket can carry a
        // URL with a query string on it, and the query string is where a token
        // would be if anybody ever put one there.
        console.error(`Could not open the world: ${refusal}`);
        if (abandoned) return;

        // Handed up rather than shown: this component cannot drop a token it does
        // not own, and a message about signing in beside a screen with no way to
        // sign in is worse than no message at all.
        if (refusal === 'unauthorised') {
          onSessionEnded();
          return;
        }

        // The same, for a refusal the player themselves can answer — but only to
        // a caller that has a screen to answer it on. See the header.
        if (onJoinRefused !== undefined) {
          onJoinRefused(refusal);
          return;
        }

        setFailure(message);
      })
      .finally(() => {
        settled = true;
        handBack();
      });

    return (): void => {
      abandoned = true;
      window.removeEventListener('pagehide', onPageHide);
      publishGameHandle(null);
      publishRoomHandle(null);
      game?.destroy(true);
      parent.replaceChildren();
      setWorld(null);
      setInvite(null);
      handBack();
    };
  }, [token, characterId, roomId, visibility, onSessionEnded, onJoinRefused, onRunEnded]);

  if (failure !== null) {
    return (
      <div className="game-fatal" role="alert" data-testid="game-fatal">
        {failure}
      </div>
    );
  }

  return (
    <div data-testid="game-stage" style={STAGE}>
      {/*
        Phaser's element, and Phaser's alone. The cleanup above empties it with
        `replaceChildren`, so anything React rendered *inside* it would be
        removed behind React's back — which is why the overlay is its sibling
        and not its child.
      */}
      <div ref={containerRef} data-testid="game-root" className="game-root" />

      {world !== null && (
        <GameOverlay
          characterClass={characterClass}
          feed={world.feed}
          intents={world.intents}
          nearestTarget={world.view.nearestTarget}
          // The one place a player can read their own code. Town cannot show it:
          // in town there is no run, and a code without a run is a code for a
          // room that no longer exists. See `ui/game-overlay.tsx`.
          invite={invite}
        />
      )}
    </div>
  );
};
